import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SpinnerOverlayService } from '../services/spinner-overlay.service';

@Injectable()
export class SpinnerInterceptor implements HttpInterceptor {
    private currentRequests: number = 0;
    private spinnerSubscription: Subscription | undefined;

    private readonly urlsSkipped: string[] = [
        'wiki/getInlineWiki',
        'wiki/isAvailable',
        'healthscore/scoreFactor',
        'user/details',
        'user/licenses',
        'user/specialPermissions',
        'user/adoGroups/',
        'user/mersypersonae/',
        'user/usermersygroupfunction/',
        'user/dataAnalytics',
        'user/enrichmgmtBranches',
        'user/dirIndirUserRoles',
        'user/branches',
        'digitalservices/dialerproperties',
        'user/groupfilterlist',
        'marketquery/market-sheet',
        'user/filter',
        'user/allrelatedroles',
        'virtualprovisioning/unittodevicemapping',
        'virtualprovisioning/unprovisionedassetbyunitid',
        'buildrelease/build/2',
        'buildrelease/release/2',
        'buildrelease/buildTDCEMT',
        'digitalServices/firmwareAssignmentStatus',
        'digitalServices/setDefaultFirmware'
    ]

    // Route templates (as declared in backend [Route]/[HttpPost] attributes).
    // {param} segments act as wildcards and the match is anchored to the end of the URL,
    // so similar prefixes (e.g. 'user/{userId}/cache') are NOT matched by mistake.
    private readonly urlsUmpLoadingOverlay: string[] = [
        'PUT user/{callerTool}/{userId}',
        'POST user/{userId}/RevokeGroupPersonae',
        'POST groups/{userId}',
        'POST groups-nottree/{userId}'
    ]
    constructor(private readonly spinnerOverlayService: SpinnerOverlayService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.skipUrls(request.url!) === false)
            this.incrementRequestCount(request.url!, request.method);

        return next.handle(request).pipe(
            // finalize will be called when the source terminates on complete or error.The specified function will also be called when the subscriber explicitly unsubscribes
            finalize(() => {
                if (this.skipUrls(request.url!) === false)
                    this.decrementRequestCount(next, request);
                if (this.currentRequests < 0)
                    this.currentRequests = 0;
            })
        );
    }

    private decrementRequestCount(next: HttpHandler, request: HttpRequest<any>) {
        if (--this.currentRequests <= 0 && this.spinnerSubscription) {
            this.spinnerSubscription.unsubscribe();
            this.spinnerSubscription = undefined;
        }
    }

    private incrementRequestCount(url: string, method: string) {
        if (this.currentRequests++ === 0) {
            const overlayType = this.isUmpLoadingOverlayUrl(url, method) ? 'ump-loading-overlay' : 'spinner';
            this.spinnerSubscription = this.spinnerOverlayService.spinner$(overlayType).subscribe();
        }
    }
    private skipUrls(url: string) {
        return this.urlsSkipped.some(x => url.toLowerCase().includes(x.toLowerCase()))
    }
    private isUmpLoadingOverlayUrl(url: string, method: string) {
        return this.urlsUmpLoadingOverlay.some(template => this.matchesRouteTemplate(url, method, template));
    }
    private matchesRouteTemplate(url: string, method: string, methodAndTemplate: string): boolean {
        const [templateMethod, ...templateParts] = methodAndTemplate.split(' ');
        const template = templateParts.join(' ');
        if (templateMethod.toUpperCase() !== method.toUpperCase())
            return false;

        // Strip query string and split into segments, then compare segment by segment.
        // A '{param}' segment in the template acts as a wildcard matching any single segment.
        const urlPath = url.toLowerCase().split('?')[0];
        const urlSegments = urlPath.split('/').filter(s => s.length > 0);
        const templateSegments = template.toLowerCase().split('/').filter(s => s.length > 0);

        if (urlSegments.length < templateSegments.length)
            return false;

        const urlTail = urlSegments.slice(urlSegments.length - templateSegments.length);
        return templateSegments.every((segment, i) =>
            (segment.startsWith('{') && segment.endsWith('}')) || segment === urlTail[i]
        );
    }
}
